import { useState, useEffect, useCallback } from 'react';
import {
  getCurrentMailItem,
  getEmailSubject,
  getEmailSender,
  getEmailItemId,
  getEmailBodyText,
} from '../services/office';

interface CurrentEmail {
  id: string | undefined;
  subject: string | undefined;
  sender: { name: string; email: string } | null;
  bodyPreview: string | null;
}

interface UseCurrentEmailResult {
  email: CurrentEmail | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCurrentEmail(): UseCurrentEmailResult {
  const [email, setEmail] = useState<CurrentEmail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmailData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const item = getCurrentMailItem();
      if (!item) {
        setEmail(null);
        return;
      }

      // Get basic info synchronously
      const id = getEmailItemId();
      const subject = getEmailSubject();
      const sender = getEmailSender();

      // Get body preview (first 200 chars)
      let bodyPreview: string | null = null;
      try {
        const bodyText = await getEmailBodyText();
        bodyPreview = bodyText.substring(0, 200).trim();
        if (bodyText.length > 200) {
          bodyPreview += '...';
        }
      } catch (e) {
        console.warn('Could not get email body preview:', e);
      }

      setEmail({
        id,
        subject,
        sender,
        bodyPreview,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load email');
      setEmail(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchEmailData();
  }, [fetchEmailData]);

  // Listen for item changes
  useEffect(() => {
    const item = getCurrentMailItem();
    if (!item) return;

    // Office.js doesn't have a great way to detect item changes in read mode,
    // so we'll rely on the taskpane being reopened or manual refresh
  }, []);

  return {
    email,
    isLoading,
    error,
    refresh: fetchEmailData,
  };
}
